import React from 'react';
import type { FractionModelPayload } from '../../core/types/payloads/fraction_model.types';
import { isVisualExerciseInstruction } from '../../core/print/visualExerciseMode';

const FILL_MINT = '#2DD4BF';
const FILL_LAVENDER = '#8A7BFA';
const NEUTRAL = '#F6F5FA';
const STROKE = '#E2E8F0';

function polarToCartesian(cx: number, cy: number, radius: number, angleDeg: number) {
  const angleRad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(angleRad),
    y: cy + radius * Math.sin(angleRad),
  };
}

function describeSlice(cx: number, cy: number, radius: number, startAngle: number, endAngle: number) {
  const start = polarToCartesian(cx, cy, radius, endAngle);
  const end = polarToCartesian(cx, cy, radius, startAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 0 ${end.x} ${end.y} Z`;
}

function FractionBarModel({ numerator, denominator }: Pick<FractionModelPayload, 'numerator' | 'denominator'>) {
  const width = 320;
  const height = 56;
  const sliceWidth = width / denominator;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-auto w-full max-w-md" role="img" aria-hidden>
      <rect x={0} y={0} width={width} height={height} rx={10} fill={NEUTRAL} stroke={STROKE} strokeWidth={1.5} />
      {Array.from({ length: denominator }, (_, index) => {
        const filled = index < numerator;
        return (
          <rect
            key={index}
            x={index * sliceWidth + 1}
            y={1}
            width={Math.max(0, sliceWidth - 2)}
            height={height - 2}
            rx={index === 0 ? 8 : index === denominator - 1 ? 8 : 2}
            fill={filled ? FILL_MINT : 'transparent'}
          />
        );
      })}
      {Array.from({ length: denominator - 1 }, (_, index) => (
        <line
          key={`divider-${index}`}
          x1={(index + 1) * sliceWidth}
          y1={4}
          x2={(index + 1) * sliceWidth}
          y2={height - 4}
          stroke={STROKE}
          strokeWidth={1}
        />
      ))}
    </svg>
  );
}

function FractionCircleModel({ numerator, denominator }: Pick<FractionModelPayload, 'numerator' | 'denominator'>) {
  const size = 220;
  const cx = size / 2;
  const cy = size / 2;
  const radius = 88;
  const sliceAngle = 360 / denominator;

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="mx-auto h-52 w-52" role="img" aria-hidden>
      <circle cx={cx} cy={cy} r={radius + 6} fill={NEUTRAL} stroke={STROKE} strokeWidth={1.5} />
      {Array.from({ length: denominator }, (_, index) => {
        const startAngle = index * sliceAngle;
        const endAngle = (index + 1) * sliceAngle;
        const filled = index < numerator;
        return (
          <path
            key={index}
            d={describeSlice(cx, cy, radius, startAngle, endAngle)}
            fill={filled ? FILL_LAVENDER : NEUTRAL}
            stroke={STROKE}
            strokeWidth={1}
          />
        );
      })}
    </svg>
  );
}

export function FractionModelView({ payload }: { payload: FractionModelPayload }) {
  const hideAnswer = isVisualExerciseInstruction(payload.instruction);

  return (
    <div className="space-y-4">
      <p className="text-sm leading-relaxed text-slate-700 print:text-[#334155]">{payload.instruction}</p>
      <div className="flex flex-col items-center gap-3">
        {payload.visualType === 'circle' ? (
          <FractionCircleModel numerator={payload.numerator} denominator={payload.denominator} />
        ) : (
          <FractionBarModel numerator={payload.numerator} denominator={payload.denominator} />
        )}
        {hideAnswer ? (
          <p className="text-lg font-semibold tabular-nums text-slate-800 print:border-b print:border-slate-400 print:px-6">
            [ ] / [ ]
          </p>
        ) : (
          <p className="text-lg font-semibold tabular-nums text-slate-800">
            {payload.numerator}/{payload.denominator}
          </p>
        )}
      </div>
    </div>
  );
}
